import type {
  ColonyOrigin,
  ColonyStatus,
  EventType,
  FeedType,
  HiveSystem,
  HoneyType,
  QueenCells,
  QueenChangeEvent,
  QueenMarkColor,
  QueenOrigin,
  QueenStatus,
  SuperType,
  TreatmentMethod,
  VarroaMethod,
} from '../db/model';

export const EVENT_LABEL: Record<EventType, string> = {
  inspection: 'Prohlídka',
  treatment: 'Léčení',
  varroa_check: 'Kontrola varroázy',
  feeding: 'Krmení',
  harvest: 'Medobraní',
  queen_change: 'Výměna matky',
  split: 'Oddělek',
  swarm: 'Rojení',
  move: 'Přesun',
  status_change: 'Změna stavu',
  super_change: 'Medníky',
  note: 'Poznámka',
};

export const HIVE_SYSTEM: Record<HiveSystem, string> = {
  adamec: '39 × 24 (Adamec)',
  langstroth: 'Langstroth',
  dadant: 'Dadant',
  optimal: 'Optimal',
  budecak: 'Budečák',
  other: 'Jiný',
};

export const COLONY_STATUS: Record<ColonyStatus, string> = {
  active: 'Žije',
  dead: 'Uhynulo',
  merged: 'Spojeno',
  sold: 'Prodáno',
  absconded: 'Vylétlo',
};

export const COLONY_ORIGIN: Record<ColonyOrigin, string> = {
  split: 'Oddělek',
  swarm: 'Roj',
  shook_swarm: 'Smetenec',
  package: 'Balíček',
  purchased: 'Koupené',
  unknown: 'Neznámý',
};

export const QUEEN_ORIGIN: Record<QueenOrigin, string> = {
  own_rearing: 'Vlastní chov',
  purchased: 'Koupená',
  swarm_cell: 'Z rojového matečníku',
  supersedure: 'Tichá výměna',
  emergency: 'Nouzový matečník',
  unknown: 'Neznámý',
};

export const QUEEN_STATUS: Record<QueenStatus, string> = {
  present: 'Ve včelstvu',
  caged: 'V kleci',
  replaced: 'Vyměněná',
  lost: 'Ztracená',
  dead: 'Uhynulá',
};

export const QUEEN_COLOR: Record<QueenMarkColor, { label: string; css: string }> = {
  white: { label: 'bílá', css: '#f4f4f0' },
  yellow: { label: 'žlutá', css: '#f2c919' },
  red: { label: 'červená', css: '#d6372b' },
  green: { label: 'zelená', css: '#3d9a4b' },
  blue: { label: 'modrá', css: '#2f6fd1' },
};

export const QUEEN_CHANGE_REASON: Record<NonNullable<QueenChangeEvent['reason']>, string> = {
  old: 'Stará',
  poor_performance: 'Slabý výkon',
  aggressive: 'Útočné',
  lost: 'Ztráta matky',
  requeening: 'Plánovaná výměna',
  other: 'Jiný',
};

export const QUEEN_CELLS: Record<QueenCells, string> = {
  none: 'Žádné',
  swarm: 'Rojové',
  supersedure: 'Tichá výměna',
  emergency: 'Nouzové',
};

export const TREATMENT_METHOD: Record<TreatmentMethod, string> = {
  fumigation: 'Fumigace',
  aerosol: 'Aerosol',
  evaporation: 'Odpařování',
  trickling: 'Pokapávání',
  sublimation: 'Sublimace',
  strips: 'Pásky',
  other: 'Jiný',
};

export const VARROA_METHOD: Record<VarroaMethod, string> = {
  bottom_board: 'Podložka',
  alcohol_wash: 'Alkoholový test',
  sugar_roll: 'Cukrový test',
  post_treatment: 'Spad po ošetření',
  winter_debris: 'Zimní měl (vyšetření)',
};

export const FEED_TYPE: Record<FeedType, string> = {
  syrup_3_2: 'Roztok 3:2',
  syrup_1_1: 'Roztok 1:1',
  invert_syrup: 'Invert',
  fondant: 'Pogača / těsto',
  other: 'Jiné',
};

export const HONEY_TYPE: Record<HoneyType, string> = {
  flower: 'Květový',
  forest: 'Lesní',
  mixed: 'Smíšený',
  rapeseed: 'Řepkový',
  acacia: 'Akátový',
  linden: 'Lipový',
  other: 'Jiný',
};

export function fmtDate(iso?: string | null): string {
  if (!iso) return '';
  // čas uložení (UTC, např. záloha ve 23:30) → datum podle místního času telefonu
  if (iso.length > 10 && iso.includes('T')) {
    const t = new Date(iso);
    if (!Number.isNaN(t.getTime())) return `${t.getDate()}. ${t.getMonth() + 1}. ${t.getFullYear()}`;
  }
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return `${d}. ${m}. ${y}`;
}

export function daysAgo(iso: string, today = new Date()): string {
  const d = new Date(`${iso}T00:00:00`);
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const n = Math.round((t.getTime() - d.getTime()) / 86400000);
  if (n === 0) return 'dnes';
  if (n === 1) return 'včera';
  if (n < 0) return `za ${-n} d`;
  if (n < 5) return `před ${n} dny`;
  return `před ${n} dny`;
}

/** „1 nový záznam“, „3 nové záznamy“, „7 nových záznamů“ (změněné od poslední zálohy). */
export const changedRecords = (n: number) =>
  n === 1 ? '1 nový nebo upravený záznam' : n >= 2 && n <= 4 ? `${n} nové nebo upravené záznamy` : `${n} nových nebo upravených záznamů`;

/** České skloňování podle počtu: 1 včelstvo, 2 včelstva, 5 včelstev. */
export function plural(n: number, one: string, few: string, many: string): string {
  return `${n} ${n === 1 ? one : n >= 2 && n <= 4 ? few : many}`;
}

export const SUPER_TYPE: Record<SuperType, string> = {
  low: 'Nízký nástavek',
  half: 'Polonástavek',
  full: 'Plný nástavek',
  other: 'Jiný',
};

/** Název včelstva bez úvodního čísla („1 Medulka“ → „Medulka“). Samotné číslo („7“) zůstane. */
export function colonyName(label: string): string;
export function colonyName(label: string | undefined | null): string | undefined;
export function colonyName(label: string | undefined | null): string | undefined {
  if (label == null) return undefined;
  const m = /^\s*\d+[a-zA-Z]?\s*[.:\-–)]?\s+(.+)$/.exec(label);
  return m ? m[1].trim() : label;
}
